import React, { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { FiChevronLeft, FiChevronRight, FiArrowRight } from "react-icons/fi";
import { fetchServices, fetchHero } from "../api";
import { DEV_TOOL_ICON_MAP } from "../data/devToolIcons";

const fallbackImages = [
  "/images/ramadhan-kareem.png",
  "/images/website-uae.png",
  "/images/ecommerce-uae.png",
  "/images/ad-uae.png",
];

export default function HomeHeroCarousel() {
  const [services, setServices] = useState([]);
  const [index, setIndex] = useState(0);
  const [typedTitle, setTypedTitle] = useState("");

  useEffect(() => {
    fetchServices().then(setServices).catch(() => {});
  }, []);

  // Home's own Hero content isn't shown here anymore (replaced by this carousel), but its
  // page_title/tagline still drive this page's browser tab title.
  useEffect(() => {
    fetchHero("home")
      .then((hero) => {
        document.title = `${hero.page_title || "Home"} - ${hero.tagline}`;
      })
      .catch(() => {
        document.title = "Home - Empowering Businesses Digitally";
      });
  }, []);

  const goTo = useCallback(
    (i) => {
      if (services.length === 0) return;
      setIndex(((i % services.length) + services.length) % services.length);
    },
    [services.length]
  );

  useEffect(() => {
    if (services.length < 2) return;
    const interval = setInterval(() => goTo(index + 1), 7000);
    return () => clearInterval(interval);
  }, [services.length, index, goTo]);

  // Typewriter effect: reveal the current slide's title one character at a time.
  useEffect(() => {
    if (services.length === 0) return;
    const text = services[index].title || "";
    setTypedTitle("");
    let i = 0;
    const typeInterval = setInterval(() => {
      i += 1;
      setTypedTitle(text.slice(0, i));
      if (i >= text.length) clearInterval(typeInterval);
    }, 70);
    return () => clearInterval(typeInterval);
  }, [services, index]);

  if (services.length === 0) return null;

  const current = services[index];
  const image = current.image || fallbackImages[index % fallbackImages.length];
  const icons = (current.icons || "").split(",").filter(Boolean);

  return (
    <section id="home" className="relative min-h-[700px] scroll-mt-20 flex items-center text-white border-b border-navy-border bg-navy overflow-hidden">
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="floating-light absolute -top-40 -left-40 w-[500px] h-[500px] rounded-full bg-accent/20 blur-[120px]" style={{ animationDuration: "18s" }} />
        <div className="floating-light absolute -bottom-40 -right-20 w-[500px] h-[500px] rounded-full bg-accent-light/10 blur-[120px]" style={{ animationDuration: "22s", animationDelay: "2s" }} />
        <div className="floating-light absolute top-1/3 left-1/2 w-[350px] h-[350px] rounded-full bg-purple-500/10 blur-[100px]" style={{ animationDuration: "26s", animationDelay: "4s" }} />
        <div className="floating-light absolute bottom-10 left-10 w-[300px] h-[300px] rounded-full bg-accent-light/10 blur-[100px]" style={{ animationDuration: "20s", animationDelay: "1s" }} />
      </div>

      <div className="relative z-10 w-full max-w-7xl mx-auto px-6 md:px-16 lg:px-[75px] py-16">
        <AnimatePresence mode="wait">
        <motion.div
            key={current.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.5 }}
            className="grid md:grid-cols-[40%_60%] gap-12 items-center"
          >
            {/* Image column */}
            <div className="flex items-center justify-center order-2 md:order-1">
              <Link to={`/services?service=${current.id}`} className="group block w-full max-w-md relative">
                <img
                  src={image}
                  alt={current.title}
                  className="w-full rounded-xl shadow-2xl object-cover transition-transform duration-300 group-hover:scale-[1.02]"
                />
                <div className="absolute inset-0 rounded-xl bg-navy/0 group-hover:bg-navy/20 transition-colors duration-300" />
              </Link>
            </div>

            {/* Content column */}
            <div className="text-center md:text-left order-1 md:order-2">
              <span className="text-accent-light uppercase tracking-widest text-sm font-semibold">
                Our Service
              </span>
              <h2 className="mt-3 text-3xl md:text-5xl font-extrabold tracking-tight">
                <span className="gradient-text-animated-warm">{typedTitle}</span>
                <span className="typing-cursor inline-block w-[3px] md:w-[4px] ml-1 bg-accent-light align-middle" style={{ height: "0.85em" }} />
              </h2>
              <p className="mt-6 text-lg text-gray-300">{current.description}</p>

              {icons.length > 0 && (
                <div className="mt-6 flex flex-wrap justify-center md:justify-start gap-3">
                  {icons.map((key) => {
                    const entry = DEV_TOOL_ICON_MAP[key];
                    if (!entry) return null;
                    const { Icon, label } = entry;
                    return (
                      <span
                        key={key}
                        title={label}
                        className="flex items-center gap-2 px-3 py-2 rounded-md bg-white/5 border border-navy-border text-white/80 text-sm"
                      >
                        <Icon size={16} />
                        {label}
                      </span>
                    );
                  })}
                </div>
              )}

              <Link
                to={`/services?service=${current.id}`}
                className="mt-8 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-brand-gradient text-white font-semibold shadow-lg shadow-accent/20 hover:shadow-accent/40 transition-shadow"
              >
                View Details <FiArrowRight />
              </Link>
            </div>
          </motion.div>
        </AnimatePresence>

        {/* Controls */}
        {services.length > 1 && (
          <div className="flex items-center justify-center gap-6 mt-12">
            <button
              onClick={() => goTo(index - 1)}
              aria-label="Previous service"
              className="w-10 h-10 flex items-center justify-center rounded-full border border-white/20 text-white hover:border-accent-light transition-colors"
            >
              <FiChevronLeft />
            </button>
            <div className="flex gap-2">
              {services.map((s, i) => (
                <button
                  key={s.id}
                  onClick={() => goTo(i)}
                  aria-label={`Go to ${s.title}`}
                  className={`w-2.5 h-2.5 rounded-full transition-colors ${
                    i === index ? "bg-accent-light" : "bg-white/20 hover:bg-white/40"
                  }`}
                />
              ))}
            </div>
            <button
              onClick={() => goTo(index + 1)}
              aria-label="Next service"
              className="w-10 h-10 flex items-center justify-center rounded-full border border-white/20 text-white hover:border-accent-light transition-colors"
            >
              <FiChevronRight />
            </button>
          </div>
        )}
      </div>
    </section>
  );
}
